<template>
    <CompassTableRow class="automation-listing__row" @click.native="handleClickRow">
        <template v-for="column in columns">
            <CompassTableBodyCell
                v-if="column.code === 'name'"
                :key="column.code"
                :maxWidth="column.maxWidth"
            >
                <div class="automation-listing__cell--name">
                    <span class="typographies-paragraph-medium-semibold">
                        {{ automation.name }}
                    </span>
                </div>
            </CompassTableBodyCell>
            <CompassTableBodyCell
                v-if="column.code === 'elements'"
                :key="column.code"
                :fixed="column.fixed"
                :maxWidth="column.maxWidth"
                :minWidth="column.minWidth"
            >
                <AutomationPieces :pieces="automation.pieces" />
            </CompassTableBodyCell>
            <CompassTableBodyCell
                v-if="column.code === 'date'"
                :key="column.code"
                :fixed="column.fixed"
                :maxWidth="column.maxWidth"
                :minWidth="column.minWidth"
            >
                <span class="typographies-paragraph-medium-regular">
                    {{ formattedDate(automation.date) }}
                </span>
            </CompassTableBodyCell>
            <CompassTableBodyCell
                v-if="column.code === 'status'"
                :key="column.code"
                :alignContent="column.alignContent"
                :fixed="column.fixed"
                :maxWidth="column.maxWidth"
                :minWidth="column.minWidth"
                class="automation-listing__cell--status"
            >
                <div class="automation-listing__status-container">
                    <CompassTooltip
                        :disabled="!isDisabledAutomationTooltip"
                        :content="translations.tooltipSwitchDisabled"
                        :arrow="false"
                    >
                        <CompassSwitch
                            :value="automation.status"
                            :disabled="isDisabledAutomation"
                            @click="handleSwitchAutomation"
                        />
                    </CompassTooltip>
                    <CompassLoader
                        v-if="isLoadingSwitch"
                        class="automation-listing__status-loader"
                    />
                </div>
            </CompassTableBodyCell>
            <CompassTableBodyCell
                v-if="column.code === 'actions'"
                :key="column.code"
                :fixed="column.fixed"
                :maxWidth="column.maxWidth"
                :minWidth="column.minWidth"
                class="automation-listing__cell--actions"
            >
                <AutomationListingTableActions
                    :automation="automation"
                    @automation-duplicated="$emit('automation-duplicated')"
                    @edit-wizard-automation="$emit('edit-wizard-automation', $event)"
                />
            </CompassTableBodyCell>
        </template>
    </CompassTableRow>
</template>

<script>
import AutomationListingTableActions from 'vcomponents/automation/listing/table/AutomationListingTableActions.vue';
import AutomationPieces from 'vcomponents/automation/AutomationPieces.vue';
import { disableAutomation, enableAutomation } from 'api/automation/automationService.js';
import Feature from 'Services/Feature';
import { createNamespacedHelpers } from 'vuex';
const { mapState: corpMapState } = createNamespacedHelpers('corp');
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    components: { AutomationListingTableActions, AutomationPieces },
    props: {
        columns: {
            type: Array,
            required: true,
        },
        automation: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            translations: {
                tooltipSwitchDisabled: this.$t.__(
                    'automation.AutomationListingTableRow.tooltip.switch_disabled',
                ),
            },
            isLoadingSwitch: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        ...corpMapState(['timeformat', 'hourformat']),
        formatDateDisplayed() {
            const timeFormat = this.timeformat === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
            const hourFormat = this.hourformat === '12' ? 'hh:mm a' : 'HH:mm';
            return `${timeFormat} ${hourFormat}`;
        },
        isDisabledAutomationTooltip() {
            return !this.automation.version.valid && !this.isLoadingSwitch;
        },
        isDisabledAutomation() {
            return !this.automation.version.valid || this.isLoadingSwitch;
        },
        isWizardAutomation() {
            return this.automation?.metadata?.wizard === true;
        },
        hasAutomationBuilderFeature() {
            return Feature.isEnabled('automations-builder');
        },
        canAccessBuilder() {
            if (this.isWizardAutomation) {
                return false;
            }
            return this.hasAutomationBuilderFeature;
        },
        canEditAutomation() {
            return this.isWizardAutomation || this.canAccessBuilder;
        },
    },
    methods: {
        formattedDate(timestamp) {
            return moment(timestamp).format(this.formatDateDisplayed);
        },
        handleClickRow() {
            const excludedClasses = [
                '.compass-icon-button',
                '.compass-switch',
                '.automation-listing__cell--status',
                '.automation-listing__cell--actions',
            ];
            if (excludedClasses.some((selector) => event.target.closest(selector))) {
                return;
            }
            if (!this.canEditAutomation) {
                return;
            }
            if (this.isWizardAutomation) {
                this.handleEditWizardAutomation();
            } else {
                this.$router.push({
                    name: 'automations.flow.builder',
                    params: { id: this.automation.id },
                });
            }
        },
        handleEditWizardAutomation() {
            this.$emit('edit-wizard-automation', this.automation);
        },
        activateAutomation() {
            return enableAutomation(this.automation.id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('automation.common.success.start_automation'),
                    });
                })
                .catch((error) => {
                    let textToast = this.$t.__('automation.common.error.stop_automation');
                    if (error.response.status === 409) {
                        textToast = this.$t.__(
                            'automation.automationListingTableRow.toast.error.wait_minute',
                        );
                    }
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: textToast,
                    });
                    throw error;
                })
                .finally(() => {
                    this.isLoadingSwitch = false;
                });
        },
        deactivateAutomation() {
            return disableAutomation(this.automation.id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('automation.common.success.stop_automation'),
                    });
                })
                .catch((error) => {
                    let textToast = this.$t.__('automation.common.error.stop_automation');
                    if (error.response.status === 409) {
                        textToast = this.$t.__(
                            'automation.automationListingTableRow.toast.error.wait_minute',
                        );
                    }
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: textToast,
                    });
                    throw error;
                })
                .finally(() => {
                    this.isLoadingSwitch = false;
                });
        },
        handleSwitchAutomation() {
            if (this.isDisabledAutomation) {
                return;
            }
            this.isLoadingSwitch = true;
            const newStatus = !this.automation.status;
            const action = newStatus ? this.activateAutomation() : this.deactivateAutomation();
            action.then(() => {
                this.$emit('update-automation', {
                    id: this.automation.id,
                    status: newStatus,
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-listing {
    &__row {
        cursor: pointer;
    }

    &__cell {
        &--name {
            user-select: none;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__status {
        &-container {
            display: flex;
            align-items: center;
            gap: var(--spacing-1);
        }

        &-loader {
            flex-shrink: 0;
        }
    }
}
</style>
